import * as Sentry from '@sentry/vue'
import OpenAIApi from '@/api/openai'
import eventCategories from '@/constant/eventCategories'
import utils from '@/util'
import mixpanelUtils from '@/utils/mixpanel.utils'
import { AI_SLIDES_GENERATOR_REFINE_RESULTS } from '@/constant/eventActions'
import EventBus from '@/eventBus'
import i18nPresenter from '@/i18nPresenter'
import getters from '@/vuex/getters'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import store from '@/vuex/store'

const AI_QUIZ_GENERATOR_SUCCESSFUL = 'AI Quiz Generator > Generate > Successful'

const PROMPT_MAX_LENGTH = 3000
const SET_IS_DONE_GENERATING = 'SET_IS_DONE_GENERATING'
const SET_IS_SENDING_AI_REQUEST = 'SET_IS_SENDING_AI_REQUEST'
const SET_IS_SENDING_AI_REQUEST_FEEDBACK = 'SET_IS_SENDING_AI_REQUEST_FEEDBACK'
const SET_USER_PROMPT = 'SET_USER_PROMPT'
const SET_IS_RETURNED_SLIDE_ERROR = 'SET_IS_RETURNED_SLIDE_ERROR'
const SET_HAS_STOPPED_GENERATING = 'SET_HAS_STOPPED_GENERATING'
const RESET_ALL_STATES_TO_DEFAULT = 'RESET_ALL_STATES_TO_DEFAULT'
const SET_SYSTEM_ERROR = 'SET_SYSTEM_ERROR'
const SET_AI_RESPONSE = 'SET_AI_RESPONSE'
const SET_STATES_WHEN_ERROR_GENERATING = 'SET_STATES_WHEN_ERROR_GENERATING'
const RESET_LOADING_AND_ERROR_STATES = 'RESET_LOADING_AND_ERROR_STATES'
const SET_AI_RESULT_MODAL_VISIBLE = 'SET_AI_RESULT_MODAL_VISIBLE'
const SET_CUSTOM_AI_SLIDE_STUMPED_TEXT = 'SET_CUSTOM_AI_SLIDE_STUMPED_TEXT'
const SET_IS_LOADING_SPINNER_HIDDEN = 'SET_IS_LOADING_SPINNER_HIDDEN'
const SET_PRESENTATION_REQUEST_ID = 'SET_PRESENTATION_REQUEST_ID'
const AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE = 'aiConversation/removeSystemMessageAndAddSuccessErrorMessageToConversationAction'
const SET_IS_NEW_PRESENTATION_ACTION = 'setIsNewPresentationAction'
const IS_EU_REGION = getters.isEURegion()

// constants
const REQUEST_MAXIMUM = 150

// TRACKING FUNCTIONS
const trackGenerateSlideFromExample = (example, rootState) => {
  if (IS_EU_REGION) return
  const eventAction = 'AI Quiz Generator > Example'
  const trackData = {
    eventAction,
    eventCategory: eventCategories.PRESENTATION_EDITOR,
    presentationId: rootState.presentation.id,
    exampleContent: example,
    userId: rootState.currentUser.id,
    touchPoint: rootState.slideGenerator.gaTouchPointWhenOpenAIModal ?
      rootState.slideGenerator.gaTouchPointWhenOpenAIModal : 'AI panel',
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackAIRequestToGenerateSlides = (prompt, rootState, mode, isExample, suggestion) => {
  try {
    if (IS_EU_REGION) return
    if (isExample) {
      trackGenerateSlideFromExample(suggestion, rootState)
      return
    }
    utils.mixpanelTracking(
      eventCategories.PRESENTATION_EDITOR,
      'AI Quiz Generator > Generate',
      prompt,
      `Mode: ${mode}`,
      {
        presentationId: rootState.presentation.id,
        touchPoint: rootState.slideGenerator.gaTouchPointWhenOpenAIModal ?
          rootState.slideGenerator.gaTouchPointWhenOpenAIModal : 'AI panel',
      },
    )
    mixpanelUtils?.trackTimeEvent('AI Quiz Generator > Generate > Failed')
    mixpanelUtils?.trackTimeEvent('AI Quiz Generator > Generate > Successful')
  } catch (error) {
    Sentry.captureException(error)
  }
}

const trackGenerateSlidesFailedAction = (state, rootState) => {
  if (IS_EU_REGION) return
  utils.mixpanelTracking(
    eventCategories.PRESENTATION_EDITOR,
    'AI Quiz Generator > Generate > Failed',
    state.userPrompt,
    null,
    {
      presentationId: rootState.presentation.id,
      touchPoint: rootState.slideGenerator.gaTouchPointWhenOpenAIModal ?
        rootState.slideGenerator.gaTouchPointWhenOpenAIModal : 'AI panel',
    },
  )
}

const trackStopGeneratingSlides = (state, rootState) => {
  if (IS_EU_REGION) return
  const eventAction = 'AI Slide Generator > Stop generating'
  const trackData = {
    eventAction,
    eventCategory: eventCategories.PRESENTATION_EDITOR,
    eventLabel: state.userPrompt,
    presentationId: rootState.presentation.id,
    userId: rootState.currentUser.id,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackGenerateAllSlidesSuccessful = (state, rootState) => {
  if (IS_EU_REGION) return
  utils.mixpanelTracking(
    eventCategories.PRESENTATION_EDITOR,
    AI_QUIZ_GENERATOR_SUCCESSFUL,
    state.userPrompt,
    null,
    {
      presentationId: rootState.presentation.id,
    },
  )
  utils.sendCustomEventToGTM(
    AI_QUIZ_GENERATOR_SUCCESSFUL,
    {
      presentationId: rootState.presentation.id,
    },
  )
}

const trackClickOnFeedbackButton = (feedback, rootState) => {
  if (IS_EU_REGION) return
  const eventAction = AI_SLIDES_GENERATOR_REFINE_RESULTS
  const trackData = {
    eventAction,
    eventLabel: feedback,
    eventCategory: eventCategories.PRESENTATION_EDITOR,
    presentationId: rootState.presentation.id,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackRetryGenerateSlides = (state, rootState) => {
  if (IS_EU_REGION) return
  const eventAction = 'AI Slide Generator > Generate > Retry'
  const trackData = {
    eventAction,
    eventCategory: eventCategories.PRESENTATION_EDITOR,
    eventLabel: state.userPrompt,
    presentationId: rootState.presentation.id,
    userId: rootState.currentUser.id,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

// OTHER FUNCTIONS
const showToastInformSlidesAdded = async (numberOfSlidesAdded) => {
  try {
    const content = i18nPresenter.tc('slideAddedToPresentation', numberOfSlidesAdded, { count: numberOfSlidesAdded })

    utils.showToastSuccess(i18nPresenter.t(content), 'number-of-slides-added-toaster', null, {
      duration: 5000,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const addToPresentationAndSetSuccessState = async (commit, dispatch, state, rootState, socketData) => {
  try {
    dispatch(SET_IS_NEW_PRESENTATION_ACTION, false, { root: true })
    await dispatch('addSlidesAction', { slides: socketData?.slides }, { root: true })
    showToastInformSlidesAdded(socketData.slides.length)
    EventBus.$emit('eventHandleSetValueTransition')
    await dispatch(AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE, { mode: 'slide-added', numberOfSlidesAdded: socketData.slides.length }, { root: true })
    commit(SET_SYSTEM_ERROR, false)
    trackGenerateAllSlidesSuccessful(state, rootState)
  } catch (error) {
    Sentry.captureException(error)
  }
}

// const setDoneStateAndUpdateUserRequestCount = async (commit, dispatch, slideGeneratorRequestCount) => {
//   await utils.sleep(1000)
//   commit(SET_IS_DONE_GENERATING, true)
//   if (slideGeneratorRequestCount) {
//     commit(UPDATE_CURRENT_USER_MUTATION, {
//       slideGeneratorRequestCount,
//     }, { root: true })
//   }
//   if (slideGeneratorRequestCount === REQUEST_THRESHOLD
//         || slideGeneratorRequestCount === REQUEST_MAXIMUM) {
//     dispatch('setAIRequestLimitModalStateAction', true, { root: true })
//   }
// }

const handleGenerateSlidesError = (state, rootState, error) => {
  trackGenerateSlidesFailedAction(state, rootState)
  Sentry.captureException(error)
}

const needToShowAIRequestLimitModal = (rootState) => {
  const { slideGeneratorRequestCount } = rootState.currentUser
  if (slideGeneratorRequestCount >= REQUEST_MAXIMUM) {
    return true
  }
  return false
}

const getAIGenerateSlidesResponse = (prompt, state) => {
  const payload = {
    presentationId: state.presentationRequestId,
    prompt,
  }

  if (store.getters['themeModule/canViewThemeGallery']) {
    return OpenAIApi.generateInitialContentV3(payload)
  }

  return OpenAIApi.generateInitialContent(payload)
}

const getAIGenerateSlidesResponseV2 = (prompt, state) => {
  const payload = {
    presentationId: state.presentationRequestId,
    prompt,
    canViewThemeGalleryFlag: store.getters['themeModule/canViewThemeGallery'],
  }
  return OpenAIApi.generateInitialContentV2(payload)
}

const scrollToResultModalBottom = async (commit, dispatch) => {
  dispatch('progressBar/incrementProgressBarAction', null, { root: true })
  await utils.sleep(500)
  const element = document.getElementById('ai-slides-generator-result-layout')
  if (!element) return
  element.scrollTo({
    top: element.scrollHeight + 200,
    behavior: 'smooth',
  })
}

const _state = {
  isDoneGenerating: true,
  isSendingAIRequest: false,
  isSendingAIRequestFeedback: false,
  userPrompt: null,
  isReturnedSlideError: false,
  hasStoppedGenerating: false,
  systemError: null,
  aiResponse: [],
  isAIResultModalVisible: false,
  customAISlideStumpedText: [],
  isLoadingSpinnerHidden: false,
  presentationRequestId: null,
}

const _getters = {
  promptMaxLength() {
    return PROMPT_MAX_LENGTH
  },
  aiRequestSlideType(_, __, rootState) {
    const { slideActive } = rootState
    if (slideActive && slideActive.id) {
      switch (slideActive.type) {
        case 'pickAnswer':
          return 'pickAnswer'
        case 'typeAnswer':
          return 'typeAnswer'
        case 'freestyle':
          return 'freestyle'
        default:
          return null
      }
    }
    return null
  },

  // STATES
  isDoneGenerating(state) {
    return state.isDoneGenerating
  },
  isSendingAIRequest(state) {
    return state.isSendingAIRequest
  },
  isSendingAIRequestFeedback(state) {
    return state.isSendingAIRequestFeedback
  },
  isAIRequestingOrGeneratingSlides(state) {
    return state.isSendingAIRequest ||
    (!state.isDoneGenerating && !state.isReturnedSlideError)
  },
  hasStoppedGenerating(state) {
    return state.hasStoppedGenerating
  },
  isAIResultModalVisible(state) {
    return state.isAIResultModalVisible
  },
  isLoadingSpinnerHidden(state) {
    return state.isLoadingSpinnerHidden
  },
  presentationRequestId: (state) => state.presentationRequestId,

  // PROMPTS
  userPrompt(state) {
    return state.userPrompt
  },
  hasUserPrompt(state) {
    return state.userPrompt && state.userPrompt.length > 0
  },

  // RESPONSEs
  aiResponse(state) {
    return state.aiResponse
  },

  // ERRORS
  isReturnedSlideError(state) {
    return state.isReturnedSlideError
  },
  systemError(state) {
    return state.systemError
  },

  // UNLEASH
  canUseAIImproveGeneratePresentation: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-ai-improve-generate-presentation')
  },
}

const actions = {
  setIsDoneGeneratingAction({ commit }, value) {
    commit(SET_IS_DONE_GENERATING, value)
  },
  setIsSendingAIRequestAction({ commit }, value) {
    commit(SET_IS_SENDING_AI_REQUEST, value)
  },
  setIsSendingAIRequestFeedbackAction({ commit }, value) {
    commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, value)
  },
  setUserPromptAction({ commit }, value) {
    commit(SET_USER_PROMPT, value)
  },
  setIsReturnedSlideErrorAction({ commit }, value) {
    commit(SET_IS_RETURNED_SLIDE_ERROR, value)
  },
  setSystemErrorAction({ commit }, value) {
    commit(SET_SYSTEM_ERROR, value)
  },
  setAIResponseAction({ commit }, value) {
    commit(SET_AI_RESPONSE, value)
  },
  setAIResultModalVisibleAction({ commit }, value) {
    commit(SET_AI_RESULT_MODAL_VISIBLE, value)
  },
  setLoadingButtonAddSlideAction({ commit, state }, payload) {
    let { response } = payload
    const { value } = payload
    response = state.aiResponse.map((res) => {
      return {
        ...res,
        isDisabled: value,
        isAddingSlide: response.id === res.id ? value : false,
      }
    })
    commit(SET_AI_RESPONSE, response)
  },
  resetPromptToSlideRequestAction({ commit }) {
    commit(SET_IS_DONE_GENERATING, true)
    commit(SET_IS_SENDING_AI_REQUEST, false)
    commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, false)
    commit(SET_PRESENTATION_REQUEST_ID, null)
  },
  async generateSlidesAction({
    commit, dispatch, state, rootState, rootGetters,
  }, payload) {
    try {
      const { mode, example } = payload
      const hasPrompt = state.userPrompt && state.userPrompt.length > 0 && state.userPrompt.trim().length > 0
      if (!state.isDoneGenerating || state.isSendingAIRequest || !hasPrompt) return
      commit(SET_PRESENTATION_REQUEST_ID, rootState.presentation.id)
      commit(RESET_ALL_STATES_TO_DEFAULT)
      if (state.isSendingAIRequest) {
        dispatch('progressBar/incrementProgressBarAction', null, { root: true })
      }

      if (needToShowAIRequestLimitModal(rootState)) {
        dispatch('setAIRequestLimitModalStateAction', true, { root: true })
        return
      }

      commit(SET_IS_SENDING_AI_REQUEST, true)
      trackAIRequestToGenerateSlides(state.userPrompt, rootState, mode, example, state.userPrompt)

      const canUseAIImproveGeneratePresentation = rootGetters['promptToSlideGenerator/canUseAIImproveGeneratePresentation']
      const res = canUseAIImproveGeneratePresentation ?
        await getAIGenerateSlidesResponseV2(state.userPrompt, state) :
        await getAIGenerateSlidesResponse(state.userPrompt, state)

      if (!res) {
        commit(SET_STATES_WHEN_ERROR_GENERATING)
        handleGenerateSlidesError(state, rootState, 'No response from OpenAI')
      }
    } catch (error) {
      await utils.sleep(500)
      commit(SET_STATES_WHEN_ERROR_GENERATING)
      await dispatch(AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE, { mode: 'error' }, { root: true })
      handleGenerateSlidesError(state, rootState, error)
    }
  },

  stopGeneratingSlidesAction({
    commit, dispatch, state, rootState,
  }) {
    try {
      commit(RESET_LOADING_AND_ERROR_STATES)
      commit(SET_HAS_STOPPED_GENERATING, true)
      dispatch(AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE, { mode: 'generation-stopped' }, { root: true })
      trackStopGeneratingSlides(state, rootState)
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  async retryAISlidesGeneratorAction({ dispatch, state, rootState }, mode) {
    try {
      dispatch('aiConversation/removeSystemMessageAction', null, { root: true })
      dispatch('generateSlidesAction', mode)
      trackRetryGenerateSlides(state, rootState)
    } catch (error) {
      await utils.sleep(500)
      dispatch(AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE, { mode: 'error' }, { root: true })
      Sentry.captureException(error)
    }
  },

  // async handleGenerateSlideSocketSuccessAction({
  //   commit, dispatch, state, rootState,
  // }, res) {
  //   try {
  //     const isCurrentRequest = res && res.presentationRequestId === state.presentationRequestId
  //     if (state.hasStoppedGenerating || !isCurrentRequest) return
  //     const hasResponse = res && res.slide && res.slide.data && res.slide.data.length
  //     const hasDone = res && res.done
  //     const { slideGeneratorRequestCount } = res

  //     commit(SET_IS_SENDING_AI_REQUEST, false)
  //     commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, false)
  //     commit(SET_IS_DONE_GENERATING, false)

  //     // HAS RESPONSE
  //     if (hasResponse) {
  //       await handleValidAIResponseAndSetSuccessStates(commit, dispatch, state, res)
  //     }

  //     // HAS RETURNED ALL RESPONSES
  //     if (!hasResponse && hasDone) {
  //       await setDoneStateAndUpdateUserRequestCount(commit, dispatch, slideGeneratorRequestCount)
  //     }

  //     if (!state.systemError) {
  //       trackGenerateAllSlidesSuccessful(state, rootState)
  //     }
  //   } catch (error) {
  //     Sentry.captureException(error)
  //   }
  // },

  async handleGenerateSlideSocketErrorAction({
    commit, dispatch, state, rootState,
  }) {
    commit(SET_IS_DONE_GENERATING, true)
    commit(SET_IS_SENDING_AI_REQUEST, false)
    commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, false)
    const noAIResponse = !state.aiResponse || (state.aiResponse && !state.aiResponse.length)
    if (noAIResponse) {
      commit(SET_AI_RESPONSE, null)
      commit(SET_SYSTEM_ERROR, true)
      await dispatch(AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE, { mode: 'error' }, { root: true })
      trackGenerateSlidesFailedAction(state, rootState)
    } else {
      commit(SET_IS_RETURNED_SLIDE_ERROR, true)
    }
  },

  async generateSlidesFromFeedbackAction({
    dispatch, commit, state, rootState,
  }, feedback) {
    try {
      if (state.isSendingAIRequestFeedback) return
      commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, true)
      commit(SET_HAS_STOPPED_GENERATING, false)
      scrollToResultModalBottom(commit, dispatch)
      trackClickOnFeedbackButton(feedback, rootState)
      const payload = {
        feedbackMessage: feedback,
        uniqueAccessCode: rootState.presentation.uniqueAccessCode,
        allQuizzes: state.aiResponse,
        userPrompt: state.userPrompt,
        presentationRequestId: state.presentationRequestId,
      }
      dispatch('slidesGeneratorFeedback/sendFeedbackOnSlidesGeneratorResult', payload, { root: true })
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_IS_DONE_GENERATING, true)
      commit(SET_IS_LOADING_SPINNER_HIDDEN, true)
      commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, false)
      commit(SET_IS_RETURNED_SLIDE_ERROR, !!state.systemError)
      commit(SET_CUSTOM_AI_SLIDE_STUMPED_TEXT, ['Cannot generate quiz at the moment.'])
    }
  },

  // INITIAL CONTENT
  async handleChatResponseSocketAction({
    commit, dispatch, state, rootState,
  }, socketData) {
    try {
      const isCurrentRequest = state.presentationRequestId === rootState.presentation.id
      if (!isCurrentRequest) return

      const hasResponse = socketData && socketData.slides && socketData.slides.length

      commit(SET_IS_DONE_GENERATING, true)
      commit(SET_IS_SENDING_AI_REQUEST, false)
      commit(SET_IS_SENDING_AI_REQUEST_FEEDBACK, false)
      if (state.hasStoppedGenerating) {
        commit(SET_HAS_STOPPED_GENERATING, false)
      }
      if (hasResponse) {
        await addToPresentationAndSetSuccessState(commit, dispatch, state, rootState, socketData)
      } else {
        commit(SET_SYSTEM_ERROR, true)
        await dispatch(AI_CONVERSATION_REMOVE_AND_ADD_SYSTEM_MESSAGE, { mode: 'error' }, { root: true })
        trackGenerateSlidesFailedAction(state, rootState)
      }
      commit(SET_IS_DONE_GENERATING, true)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

const mutations = {
  [SET_IS_DONE_GENERATING](state, value) {
    state.isDoneGenerating = value
  },
  [SET_IS_SENDING_AI_REQUEST](state, value) {
    state.isSendingAIRequest = value
  },
  [SET_IS_SENDING_AI_REQUEST_FEEDBACK](state, value) {
    state.isSendingAIRequestFeedback = value
  },
  [SET_USER_PROMPT](state, value) {
    state.userPrompt = value
  },
  [SET_IS_RETURNED_SLIDE_ERROR](state, value) {
    state.isReturnedSlideError = value
  },
  [SET_HAS_STOPPED_GENERATING](state, value) {
    state.hasStoppedGenerating = value
  },
  [SET_SYSTEM_ERROR](state, value) {
    state.systemError = value
  },
  [SET_AI_RESPONSE](state, value) {
    state.aiResponse = value
  },
  [RESET_ALL_STATES_TO_DEFAULT](state) {
    state.hasStoppedGenerating = false
    state.isDoneGenerating = false
    state.isReturnedSlideError = false
    state.systemError = false
    state.aiResponse = []
  },
  [SET_STATES_WHEN_ERROR_GENERATING](state) {
    state.isDoneGenerating = true
    state.isSendingAIRequest = false
    state.systemError = true
  },
  [RESET_LOADING_AND_ERROR_STATES](state) {
    state.isDoneGenerating = true
    state.isSendingAIRequest = false
    state.isSendingAIRequestFeedback = false
    state.systemError = false
    state.isReturnedSlideError = false
  },
  [SET_AI_RESULT_MODAL_VISIBLE](state, value) {
    state.isAIResultModalVisible = value
  },
  [SET_CUSTOM_AI_SLIDE_STUMPED_TEXT](state, value) {
    state.customAISlideStumpedText = value
  },
  [SET_IS_LOADING_SPINNER_HIDDEN](state, value) {
    state.isLoadingSpinnerHidden = value
  },
  [SET_PRESENTATION_REQUEST_ID](state, value) {
    state.presentationRequestId = value
  },
}

const openAI = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default openAI
